import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const MAX_FILE_SIZE = 600 * 1024 * 1024; // 600MB

type FileUploadSectionProps = {
  requestId?: number;
  artworkId?: number;
  onUploadComplete: () => void;
  isProcessed?: boolean;
  context?: 'digitization' | 'artwork';
};

export function FileUploadSection({
  requestId,
  artworkId,
  onUploadComplete,
  isProcessed = false,
  context = 'digitization'
}: FileUploadSectionProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [useNewFunction, setUseNewFunction] = useState(true);

  // Format file size to human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`);
      return;
    }

    setIsUploading(true);
    setShowProgress(true);
    setUploadProgress(0);
    let tempFilePath: string | null = null;

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => prev < 90 ? prev + 5 : prev);
      }, 500);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('temp_uploads')
        .upload(`${context}/${Date.now()}-${file.name}`, file, { upsert: false });

      clearInterval(progressInterval);
      if (uploadError) throw uploadError;

      tempFilePath = uploadData.path;
      const { data: { publicUrl } } = supabase.storage
        .from('temp_uploads')
        .getPublicUrl(tempFilePath);

      setUploadProgress(100);
      setTimeout(() => setShowProgress(false), 2000);

      toast.info(`Processing ${context === 'digitization' ? (isProcessed ? 'processed' : 'raw') : 'artwork'} file...`);

      const { data, error: processError } = await supabase.functions.invoke<{
        highres_filename: string;
        dimensions: { width: number; height: number };
        color_space: string | null;
        color_profile: string | null;
        weight: number | null;
        preview_url: string;
      }>('digitization-upload', {
        body: {
          publicUrl,
          originalFilename: file.name
        }
      });

      if (processError) throw processError;
      if (!data) throw new Error('No data returned from image processing');

      if (context === 'digitization') {
        // Handle digitization request update
        const { data: fileData, error: fileError } = await supabase
          .from('highres_files')
          .insert({
            system_filename: data.highres_filename,
            original_filename: file.name,
            width_px: data.dimensions.width,
            height_px: data.dimensions.height,
            color_space: data.color_space,
            color_profile: data.color_profile,
            file_weight: data.weight
          })
          .select()
          .single();

        if (fileError) throw fileError;
        if (!fileData) throw new Error('Failed to insert file data');

        const updateData = isProcessed ? {
          processed_file_id: fileData.file_id,
          preview: data.preview_url,
          status: 'in_progress' as const
        } : {
          raw_file_id: fileData.file_id
        };

        const { error: updateError } = await supabase
          .from('digitization_requests')
          .update(updateData)
          .eq('request_id', requestId);

        if (updateError) throw updateError;
        toast.success(`${isProcessed ? 'Processed' : 'Raw'} file uploaded successfully`);

      } else if (context === 'artwork' && artworkId) {
        // Handle artwork update
        const { data: fileData, error: fileError } = await supabase
          .from('highres_files')
          .insert({
            system_filename: data.highres_filename,
            original_filename: file.name,
            width_px: data.dimensions.width,
            height_px: data.dimensions.height,
            color_space: data.color_space,
            color_profile: data.color_profile,
            file_weight: data.weight
          })
          .select()
          .single();

        if (fileError) throw fileError;
        if (!fileData) throw new Error('Failed to insert file data');

        // Use the new RPC function to update artwork files
        const { error: updateError } = await supabase.rpc('update_artwork_files', {
          p_artwork_id: artworkId,
          p_highres_file: fileData.file_id,
          p_preview: data.preview_url
        });

        if (updateError) throw updateError;
        toast.success('Artwork file updated successfully');
      }

      onUploadComplete();

    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = `Failed to upload file: ${error.message}`;

      if (error.message?.includes('timeout') || error.message?.includes('timed out')) {
        errorMessage = `Upload timed out. The file might be too large or the server may be experiencing high load. Try again later.`;
      } else if (error.status === 413 || error.message?.includes('too large')) {
        errorMessage = `File too large. Maximum allowed size is ${formatFileSize(MAX_FILE_SIZE)}.`;
      }
      toast.error(errorMessage);
    } finally {
      if (tempFilePath) {
        try {
          const { error: removeError } = await supabase.storage
            .from('temp_uploads')
            .remove([tempFilePath]);
          
          if (removeError) {
            console.error('Failed to remove temporary file:', removeError);
          } else {
            console.log('Temporary file removed successfully:', tempFilePath);
          }
        } catch (cleanupError) {
          console.error('Error during temp file cleanup:', cleanupError);
        }
      }
      setIsUploading(false);
      setShowProgress(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4">
        <input 
          type="file" 
          accept="image/*" 
          onChange={handleFileUpload} 
          className="hidden" 
          id={`file-upload-${context}-${isProcessed ? 'processed' : 'raw'}`} 
          disabled={isUploading} 
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                onClick={() => document.getElementById(`file-upload-${context}-${isProcessed ? 'processed' : 'raw'}`)?.click()} 
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload {context === 'digitization' ? (isProcessed ? 'Processed' : 'Raw') : 'High-Res'} File
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <div className="text-sm">
                <p>Maximum file size: {formatFileSize(MAX_FILE_SIZE)}.</p>
                <p>Accepted formats: JPG, PNG, TIFF and other image formats.</p>
                <p>Large files may take several minutes to process.</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {context === 'digitization' && (
          <div className="flex items-center gap-2">
            <label className="flex items-center space-x-2 text-sm">
              <input 
                type="checkbox" 
                checked={useNewFunction} 
                onChange={() => setUseNewFunction(!useNewFunction)} 
                className="h-4 w-4 rounded border-gray-300" 
              />
              <span>Use new upload</span>
            </label>
          </div>
        )}
        
        {isUploading && showProgress && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{uploadProgress}%</span>
          </div>
        )}
      </div>
      
      {isUploading && showProgress && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
          <div 
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}
    </div>
  );
}
